import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import {
  EmptyEnquiry,
  enquiryDtoStrictValidation,
  IEnquiryDto,
  IEnquiryOptionDto,
} from '../model';
import { DateOnly } from '../utility';
import { useTranslation } from 'react-i18next';
import { StyledButton, StyledInput } from '../ui';
import { IEnquiryState } from './state';
import { useEffect } from 'react';

interface IFormProps {
  enquiryState: IEnquiryState;
  roomOptions: IEnquiryOptionDto[];
  roomCategoryCount: number;
  treatmentOptions: IEnquiryOptionDto[];
  packageCount: number;
  pricingOptions: IEnquiryOptionDto[];
  code?: string | null;

  onSubmit?: (data: IEnquiryDto) => Promise<unknown>;
}

export function Form(props: IFormProps) {
  const { t, i18n } = useTranslation('anfrage-sitaram');
  const initialPersonCount = props.roomOptions[props.roomCategoryCount + 1]
    ?.valueEnabled
    ? 2
    : 1;

  const { control, handleSubmit, reset, formState, setValue, watch } =
    useForm<IEnquiryDto>({
      resolver: yupResolver(enquiryDtoStrictValidation(t)),
      defaultValues: {
        ...EmptyEnquiry,
        from: new DateOnly(new Date('2026-07-18T00:00:00')),
        to: new DateOnly(new Date('2026-07-25T00:00:00')),
        personCount: initialPersonCount,
        contactLanguage: i18n.language,
        roomOptions: props.roomOptions,
        treatmentOptions: props.treatmentOptions,
        pricingOptions: props.pricingOptions,
        promoCode: (props.code === '' ? null : props.code) ?? null,
      },
      mode: 'onChange',
    });

  const treatmentWatch = watch('treatmentOptions');
  const roomWatch = watch('roomOptions');

  const roomCategories = roomWatch.slice(0, props.roomCategoryCount);
  const occupancyOptions = roomWatch.slice(props.roomCategoryCount);
  const visiblePackageCount = props.packageCount - 1;

  useEffect(() => {
    props.enquiryState.isFormValid(formState.isValid);
  }, [formState.isValid, props.enquiryState]);

  const setTreatmentSelected = (selectedIndex: number) => {
    if (selectedIndex < props.packageCount) {
      const retreatOnlyIndex = props.packageCount - 1;
      const shouldSelectRetreatOnly =
        selectedIndex < retreatOnlyIndex &&
        treatmentWatch[selectedIndex]?.valueEnabled;
      const nextSelectedIndex = shouldSelectRetreatOnly
        ? retreatOnlyIndex
        : selectedIndex;

      for (let index = 0; index < props.packageCount; index++) {
        setValue(
          `treatmentOptions.${index}.valueEnabled`,
          index === nextSelectedIndex,
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        );
      }
      return;
    }

    setValue(
      `treatmentOptions.${selectedIndex}.valueEnabled`,
      !treatmentWatch[selectedIndex]?.valueEnabled,
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const setRoomSelected = (
    selectedIndex: number,
    offset = 0,
    count: number
  ) => {
    for (let index = 0; index < count; index++) {
      setValue(
        `roomOptions.${offset + index}.valueEnabled`,
        index === selectedIndex,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    }

    if (offset === props.roomCategoryCount) {
      setValue('personCount', selectedIndex === 1 ? 2 : 1, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <form
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10 lg:p-12"
      onSubmit={handleSubmit((data) => {
        if (props.onSubmit) {
          return props.onSubmit(data).then(() => reset(data));
        }
      })}
    >
      <h2 className="font-serif text-4xl font-medium text-brand-cashmere-900">
        {t('form.title')}
      </h2>
      <p className="mt-2 text-sm font-light text-slate-700">
        {t('form.subtitle')}
      </p>

      <SectionLabel>{t('sections.package')}</SectionLabel>

      <div className="mb-6 flex gap-4 rounded-xl border border-slate-200 bg-brand-cashmere-50 p-5 text-sm leading-6 text-brand-cashmere-900">
        <p>
          <strong className="font-medium">{t('package-hint.title')}</strong>{' '}
          {t('package-hint.description')}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {treatmentWatch.slice(0, visiblePackageCount).map((option, index) => (
          <button
            key={option.name}
            type="button"
            onClick={() => setTreatmentSelected(index)}
            className={classNames(
              'relative min-h-28 rounded-sm border p-4 text-left transition duration-200',
              option.valueEnabled
                ? 'border-brand-green-700 bg-white shadow-lg shadow-slate-900/5'
                : 'border-slate-200 bg-slate-50 hover:border-brand-green-700 hover:bg-white'
            )}
          >
            {option.valueEnabled && (
              <span className="absolute right-3 top-3 flex size-6 items-center justify-center rounded-full bg-brand-green-700 text-xs text-white">
                ✓
              </span>
            )}
            {t(`packages.badges.${index}`) && !option.valueEnabled && (
              <span className="absolute right-3 top-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-green-700">
                {t(`packages.badges.${index}`)}
              </span>
            )}
            <span className="block pr-12 font-serif text-xl font-medium leading-tight text-brand-cashmere-900">
              {option.name}
            </span>
            <span className="mt-2 block text-xs leading-5 text-slate-700">
              {t(`packages.descriptions.${index}`)}
            </span>
          </button>
        ))}
      </div>

      <SectionLabel>{t('sections.room')}</SectionLabel>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-700">
            {t('room-select.category')}
          </label>
          <select
            className="w-full rounded-sm border-slate-200 bg-slate-50 text-sm text-slate-900 focus:border-brand-green-700 focus:ring-brand-green-700"
            value={Math.max(
              0,
              roomCategories.findIndex((option) => option.valueEnabled)
            )}
            onChange={(event) =>
              setRoomSelected(
                Number(event.target.value),
                0,
                props.roomCategoryCount
              )
            }
          >
            {roomCategories.map((option, index) => (
              <option key={option.name} value={index}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-700">
            {t('room-select.occupancy')}
          </label>
          <select
            className="w-full rounded-sm border-slate-200 bg-slate-50 text-sm text-slate-900 focus:border-brand-green-700 focus:ring-brand-green-700"
            value={Math.max(
              0,
              occupancyOptions.findIndex((option) => option.valueEnabled)
            )}
            onChange={(event) =>
              setRoomSelected(
                Number(event.target.value),
                props.roomCategoryCount,
                occupancyOptions.length
              )
            }
          >
            {occupancyOptions.map((option, index) => (
              <option key={option.name} value={index}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Controller
          name="from"
          control={control}
          render={(s) =>
            StyledInput({
              ...s,
              title: t('traveldata.from'),
              inputType: 'date',
              hint: '*',
            })
          }
        />

        <Controller
          name="to"
          control={control}
          render={(s) =>
            StyledInput({
              ...s,
              title: t('traveldata.to'),
              inputType: 'date',
              hint: '*',
            })
          }
        />
      </div>

      <SectionLabel>{t('sections.contact')}</SectionLabel>

      <div className="grid gap-4 sm:grid-cols-2">
        <Controller
          name="contactFirstname"
          control={control}
          render={(s) =>
            StyledInput({
              ...s,
              title: t('personal.firstname'),
              autoComplete: 'given-name',
              hint: '*',
            })
          }
        />

        <Controller
          name="contactLastname"
          control={control}
          render={(s) =>
            StyledInput({
              ...s,
              title: t('personal.lastname'),
              autoComplete: 'family-name',
              hint: '*',
            })
          }
        />

        <Controller
          name="contactMail"
          control={control}
          render={(s) =>
            StyledInput({
              ...s,
              title: t('contact.mail'),
              autoComplete: 'email',
              inputType: 'email',
              hint: '*',
            })
          }
        />

        <Controller
          name="contactPhone"
          control={control}
          render={(s) =>
            StyledInput({
              ...s,
              title: t('contact.call'),
              autoComplete: 'tel',
              placeholder: '+43',
              hint: '*',
            })
          }
        />
      </div>

      <SectionLabel>{t('sections.addons')}</SectionLabel>

      <button
        type="button"
        onClick={() => setTreatmentSelected(props.packageCount)}
        className={classNames(
          'mb-3 flex w-full gap-4 rounded-sm border p-4 text-left transition',
          treatmentWatch[props.packageCount]?.valueEnabled
            ? 'border-brand-green-700 bg-white'
            : 'border-slate-200 bg-slate-50 hover:border-brand-green-700'
        )}
      >
        <span
          className={classNames(
            'mt-1 flex size-5 shrink-0 items-center justify-center border',
            treatmentWatch[props.packageCount]?.valueEnabled
              ? 'border-brand-green-700 bg-brand-green-700 text-white'
              : 'border-slate-200 bg-white'
          )}
        >
          {treatmentWatch[props.packageCount]?.valueEnabled ? '✓' : ''}
        </span>
        <span>
          <span className="block font-serif text-xl font-medium text-brand-cashmere-900">
            {t('addons.consultation.title')}
          </span>
          <span className="mt-1 block text-xs leading-5 text-slate-700">
            {t('addons.consultation.description')}
          </span>
        </span>
      </button>

      <Controller
        name="airportTransfer"
        control={control}
        render={(s) => (
          <button
            type="button"
            onClick={() => s.field.onChange(!s.field.value)}
            className={classNames(
              'flex w-full gap-4 rounded-sm border p-4 text-left transition',
              s.field.value
                ? 'border-brand-green-700 bg-white'
                : 'border-slate-200 bg-slate-50 hover:border-brand-green-700'
            )}
          >
            <span
              className={classNames(
                'mt-1 flex size-5 shrink-0 items-center justify-center border',
                s.field.value
                  ? 'border-brand-green-700 bg-brand-green-700 text-white'
                  : 'border-slate-200 bg-white'
              )}
            >
              {s.field.value ? '✓' : ''}
            </span>
            <span>
              <span className="block font-serif text-xl font-medium text-brand-cashmere-900">
                {t('addons.transfer.title')}{' '}
                <span className="align-middle text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-green-700">
                  {t('addons.transfer.badge')}
                </span>
              </span>
              <span className="mt-1 block text-xs leading-5 text-slate-700">
                {t('addons.transfer.description')}
              </span>
            </span>
          </button>
        )}
      />

      <SectionLabel>{t('sections.message')}</SectionLabel>

      <Controller
        name="message"
        control={control}
        render={({ field, fieldState }) => (
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-700">
              {t('message.title')}
            </label>
            <textarea
              id={field.name}
              name={field.name}
              value={field.value ?? ''}
              onChange={(event) => field.onChange(event)}
              rows={5}
              placeholder={t('message.placeholder')}
              className={classNames(
                'w-full rounded-sm border bg-slate-50 text-sm text-slate-900 focus:border-brand-green-700 focus:ring-brand-green-700',
                fieldState.invalid ? 'border-red-300' : 'border-slate-200'
              )}
            />
            {fieldState.invalid && (
              <p className="mt-2 text-sm text-red-600">
                {fieldState.error?.message}
              </p>
            )}
          </div>
        )}
      />

      <div className="mt-6">
        <Controller
          name="newsletter"
          control={control}
          render={(s) => (
            <button
              type="button"
              onClick={() => s.field.onChange(!s.field.value)}
              className={classNames(
                'flex w-full gap-4 rounded-sm border p-4 text-left transition',
                s.field.value
                  ? 'border-brand-green-700 bg-white'
                  : 'border-slate-200 bg-slate-50 hover:border-brand-green-700'
              )}
            >
              <span
                className={classNames(
                  'mt-1 flex size-5 shrink-0 items-center justify-center border',
                  s.field.value
                    ? 'border-brand-green-700 bg-brand-green-700 text-white'
                    : 'border-slate-200 bg-white'
                )}
              >
                {s.field.value ? '✓' : ''}
              </span>
              <span>
                <span className="block font-serif text-xl font-medium text-brand-cashmere-900">
                  {t('newsletter.title')}
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-700">
                  {t('newsletter.message')}
                </span>
              </span>
            </button>
          )}
        />
      </div>

      <div className="mt-9 border-t border-slate-200 pt-7">
        <StyledButton
          type="submit"
          disabled={
            formState.isSubmitting || formState.isLoading || !formState.isValid
          }
          design="primary"
          pulse={formState.isValid}
          rootClassName="w-full"
          className="w-full justify-center rounded-full"
        >
          {t('action')}
        </StyledButton>
        <p className="mx-auto mt-4 max-w-xl text-center text-xs leading-5 text-slate-700">
          {t('form.disclaimer')}
        </p>
      </div>
    </form>
  );
}

function SectionLabel(props: { children: React.ReactNode }) {
  return (
    <div className="my-8 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.28em] text-brand-green-700">
      <span>{props.children}</span>
      <span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

export default Form;
